import React from 'react';
import { 
  Settings,
  FileText,
  Shield,
  Bell,
  MapPin,
  Calendar,
  Clock,
  Mail,
  MessageSquare,
  CheckCircle,
  Info,
  AlertCircle,
  Camera,
  Lock,
  Edit3,
  Ban,
  Globe
} from 'lucide-react';

const AdvancedSettings = ({ 
  settings,
  handleSettingChange,
  currentCompany
}) => {

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3 flex items-start gap-2">
        <Info size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="text-blue-300 font-medium mb-1">Advanced Configuration</p>
          <p className="text-blue-200">
            Configure default values, business rules, and notification settings for your rental system. These settings affect all reservations and system behavior.
          </p>
        </div>
      </div>

      {/* DEFAULT VALUES */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-orange-900/30 to-red-900/30">
          <h3 className="text-xl font-semibold text-white flex items-center gap-3">
            <FileText className="w-6 h-6 text-orange-500" />
            Default Values
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            Set default values for new reservations
          </p>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <MapPin size={16} className="text-orange-400" />
                Default Pickup Location
              </label>
              <select
                value={settings.defaultPickupLocation}
                onChange={(e) => handleSettingChange('defaultPickupLocation', e.target.value)}
                className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="SLBR">SLBR</option>
                <option value="Custom Location">Custom Location</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Pre-selected pickup location for new reservations
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <MapPin size={16} className="text-orange-400" />
                Default Dropoff Location
              </label>
              <select
                value={settings.defaultDropoffLocation}
                onChange={(e) => handleSettingChange('defaultDropoffLocation', e.target.value)}
                className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="SLBR">SLBR</option>
                <option value="Custom Location">Custom Location</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Pre-selected dropoff location for new reservations
              </p>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle size={14} className="text-green-400" />
              <span className="text-gray-400">Current defaults:</span>
              <span className="text-white font-medium">
                Pickup: {settings.defaultPickupLocation} • Dropoff: {settings.defaultDropoffLocation}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* BUSINESS RULES */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-orange-900/30 to-red-900/30">
          <h3 className="text-xl font-semibold text-white flex items-center gap-3">
            <Shield className="w-6 h-6 text-orange-500" />
            Business Rules
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            Configure rental policies and restrictions
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Time and Duration Settings */}
          <div>
            <h4 className="text-white font-medium mb-4 flex items-center gap-2">
              <Calendar size={16} className="text-orange-400" />
              Booking Time Restrictions
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Advance Booking (Days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={settings.advanceBookingDays}
                  onChange={(e) => handleSettingChange('advanceBookingDays', parseInt(e.target.value))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  How far in advance customers can book (1-365 days)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Max Rental Duration (Days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={settings.maxRentalDuration}
                  onChange={(e) => handleSettingChange('maxRentalDuration', parseInt(e.target.value))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum rental period allowed (1-365 days)
                </p>
              </div>
            </div>
          </div>

          {/* Required Information */}
          <div>
            <h4 className="text-white font-medium mb-4 flex items-center gap-2">
              <AlertCircle size={16} className="text-orange-400" />
              Required Customer Information
            </h4>
            <div className="space-y-3">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={settings.requireCustomerEmail}
                  onChange={(e) => handleSettingChange('requireCustomerEmail', e.target.checked)}
                  className="mr-3 mt-1 text-orange-500"
                />
                <div>
                  <span className="text-gray-300 flex items-center gap-2">
                    <Mail size={14} className="text-gray-400" />
                    Require customer email address
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Make email mandatory for all reservations
                  </p>
                </div>
              </label>
              
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={settings.requireLicenseNumber}
                  onChange={(e) => handleSettingChange('requireLicenseNumber', e.target.checked)}
                  className="mr-3 mt-1 text-orange-500"
                />
                <div>
                  <span className="text-gray-300 flex items-center gap-2">
                    <Shield size={14} className="text-gray-400" />
                    Require license number
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Make driving license number mandatory for all rentals
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Reservation Management */}
          <div>
            <h4 className="text-white font-medium mb-4 flex items-center gap-2">
              <Edit3 size={16} className="text-orange-400" />
              Reservation Management
            </h4>
            <div className="space-y-3">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={settings.allowCancellations}
                  onChange={(e) => handleSettingChange('allowCancellations', e.target.checked)}
                  className="mr-3 mt-1 text-orange-500"
                />
                <div>
                  <span className="text-gray-300 flex items-center gap-2">
                    <Ban size={14} className="text-gray-400" />
                    Allow reservation cancellations
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable customers and staff to cancel reservations
                  </p>
                </div>
              </label>
              
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={settings.allowModifications}
                  onChange={(e) => handleSettingChange('allowModifications', e.target.checked)}
                  className="mr-3 mt-1 text-orange-500"
                />
                <div>
                  <span className="text-gray-300 flex items-center gap-2">
                    <Edit3 size={14} className="text-gray-400" />
                    Allow reservation modifications
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable editing of existing reservations after creation
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Document & Camera Settings */}
          <div>
            <h4 className="text-white font-medium mb-4 flex items-center gap-2">
              <Camera size={16} className="text-orange-400" />
              Document Upload Settings
            </h4>
            <div className="space-y-3">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={settings.allowCameraCapture}
                  onChange={(e) => handleSettingChange('allowCameraCapture', e.target.checked)}
                  className="mr-3 mt-1 text-orange-500"
                />
                <div>
                  <span className="text-gray-300 flex items-center gap-2">
                    <Camera size={14} className="text-gray-400" />
                    Allow camera capture for documents
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Enable customers to take photos directly using their device camera
                  </p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={settings.showCalculationDetails}
                  onChange={(e) => handleSettingChange('showCalculationDetails', e.target.checked)}
                  className="mr-3 mt-1 text-orange-500"
                />
                <div>
                  <span className="text-gray-300 flex items-center gap-2">
                    <Info size={14} className="text-gray-400" />
                    Show calculation details to customers
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Display breakdown of rental calculations and pricing
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* System Settings */}
          <div>
            <h4 className="text-white font-medium mb-4 flex items-center gap-2">
              <Lock size={16} className="text-orange-400" />
              System Security
            </h4>
            <div className="space-y-3">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={settings.dataIsolationMode}
                  onChange={(e) => handleSettingChange('dataIsolationMode', e.target.checked)}
                  className="mr-3 mt-1 text-orange-500"
                  disabled
                />
                <div>
                  <span className="text-gray-300 flex items-center gap-2">
                    <Lock size={14} className="text-gray-400" />
                    Data isolation mode (Company-specific)
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    Automatically enabled - Each company's data is completely isolated
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Business Rules Summary */}
          <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <CheckCircle size={16} className="text-orange-400" />
              Business Rules Summary
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-gray-400">Advance Booking:</span>
                <span className="text-white ml-2 font-medium">
                  {settings.advanceBookingDays} days
                </span>
              </div>
              <div>
                <span className="text-gray-400">Max Duration:</span>
                <span className="text-white ml-2 font-medium">
                  {settings.maxRentalDuration} days
                </span>
              </div>
              <div>
                <span className="text-gray-400">Email Required:</span>
                <span className={`ml-2 font-medium ${settings.requireCustomerEmail ? 'text-orange-400' : 'text-gray-500'}`}>
                  {settings.requireCustomerEmail ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">License Required:</span>
                <span className={`ml-2 font-medium ${settings.requireLicenseNumber ? 'text-orange-400' : 'text-gray-500'}`}>
                  {settings.requireLicenseNumber ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Cancellations:</span>
                <span className={`ml-2 font-medium ${settings.allowCancellations ? 'text-green-400' : 'text-red-400'}`}>
                  {settings.allowCancellations ? 'Allowed' : 'Blocked'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Modifications:</span>
                <span className={`ml-2 font-medium ${settings.allowModifications ? 'text-green-400' : 'text-red-400'}`}>
                  {settings.allowModifications ? 'Allowed' : 'Blocked'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NOTIFICATIONS */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-orange-900/30 to-red-900/30">
          <h3 className="text-xl font-semibold text-white flex items-center gap-3">
            <Bell className="w-6 h-6 text-orange-500" />
            Notifications
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            Configure notification preferences and reminders
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Notification Channels */}
          <div>
            <h4 className="text-white font-medium mb-4 flex items-center gap-2">
              <MessageSquare size={16} className="text-orange-400" />
              Notification Channels
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={settings.enableEmailNotifications}
                    onChange={(e) => handleSettingChange('enableEmailNotifications', e.target.checked)}
                    className="mr-3 mt-1 text-orange-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-white font-medium mb-1">
                      <Mail size={16} className="text-blue-400" />
                      Email Notifications
                    </div>
                    <p className="text-xs text-gray-500">
                      Send reservation confirmations and updates via email
                    </p>
                    {settings.enableEmailNotifications && (
                      <div className="mt-2 text-xs text-green-400 bg-green-900/20 p-2 rounded border border-green-700">
                        ✓ Email notifications are active
                      </div>
                    )}
                  </div>
                </label>
              </div>
              
              <div className="bg-gray-700/30 rounded-lg p-4 border border-gray-600">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={settings.enableWhatsappNotifications}
                    onChange={(e) => handleSettingChange('enableWhatsappNotifications', e.target.checked)}
                    className="mr-3 mt-1 text-orange-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-white font-medium mb-1">
                      <MessageSquare size={16} className="text-green-400" />
                      WhatsApp Notifications
                    </div>
                    <p className="text-xs text-gray-500">
                      Send reservation updates via WhatsApp messages
                    </p>
                    {settings.enableWhatsappNotifications && (
                      <div className="mt-2 text-xs text-green-400 bg-green-900/20 p-2 rounded border border-green-700">
                        ✓ WhatsApp notifications are active
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>
          </div>
          
          {/* Reminder Settings */}
          <div>
            <h4 className="text-white font-medium mb-4 flex items-center gap-2">
              <Clock size={16} className="text-orange-400" />
              Reminder Settings
            </h4>
            <div className="max-w-md">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Reminder Days Before Pickup
              </label>
              <input
                type="number"
                min="0"
                max="7"
                value={settings.reminderDaysBefore}
                onChange={(e) => handleSettingChange('reminderDaysBefore', parseInt(e.target.value))}
                className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Send reminder notification this many days before pickup (0-7 days)
              </p>
              
              {settings.reminderDaysBefore > 0 && (
                <div className="mt-3 bg-blue-900/20 border border-blue-700 rounded p-3">
                  <div className="flex items-start gap-2">
                    <Bell size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-300">
                      Customers will receive a reminder {settings.reminderDaysBefore} {settings.reminderDaysBefore === 1 ? 'day' : 'days'} before their pickup date
                      {(settings.enableEmailNotifications || settings.enableWhatsappNotifications) && (
                        <span> via {[
                          settings.enableEmailNotifications && 'email',
                          settings.enableWhatsappNotifications && 'WhatsApp'
                        ].filter(Boolean).join(' and ')}</span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notifications Summary */}
          <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <CheckCircle size={16} className="text-orange-400" />
              Notifications Summary
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div>
                <span className="text-gray-400">Email:</span>
                <span className={`ml-2 font-medium ${settings.enableEmailNotifications ? 'text-green-400' : 'text-red-400'}`}>
                  {settings.enableEmailNotifications ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">WhatsApp:</span>
                <span className={`ml-2 font-medium ${settings.enableWhatsappNotifications ? 'text-green-400' : 'text-red-400'}`}>
                  {settings.enableWhatsappNotifications ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Reminders:</span>
                <span className={`ml-2 font-medium ${settings.reminderDaysBefore > 0 ? 'text-orange-400' : 'text-gray-500'}`}>
                  {settings.reminderDaysBefore > 0 ? `${settings.reminderDaysBefore} days` : 'Disabled'}
                </span>
              </div>
            </div>
          </div>

          {/* Help Text */}
          <div className="text-xs text-gray-500 bg-gray-700/30 rounded p-3 border border-gray-600">
            <p className="font-medium text-gray-400 mb-1">💡 Notification Tips:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Enable at least one notification channel to keep customers informed</li>
              <li>Email notifications are reliable for confirmations and receipts</li>
              <li>WhatsApp notifications provide instant delivery for time-sensitive updates</li>
              <li>Set reminders 1-2 days before pickup for best results</li>
              <li>Test notifications after enabling to ensure proper delivery</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Overall Advanced Settings Summary */}
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-lg p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Globe size={20} className="text-purple-400" />
          Advanced Configuration Overview
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800/50 rounded p-3 border border-gray-600">
            <div className="text-purple-300 font-medium mb-1 text-sm">Location Defaults</div>
            <div className="text-white text-xs">
              {settings.defaultPickupLocation === settings.defaultDropoffLocation ? 'Same location' : 'Different locations'}
            </div>
          </div>
          <div className="bg-gray-800/50 rounded p-3 border border-gray-600">
            <div className="text-purple-300 font-medium mb-1 text-sm">Booking Window</div>
            <div className="text-white text-xs">
              {settings.advanceBookingDays} days advance
            </div>
          </div>
          <div className="bg-gray-800/50 rounded p-3 border border-gray-600">
            <div className="text-purple-300 font-medium mb-1 text-sm">Flexibility</div>
            <div className="text-white text-xs">
              {settings.allowCancellations && settings.allowModifications ? 'Full' : 
               settings.allowCancellations || settings.allowModifications ? 'Partial' : 'Restricted'}
            </div>
          </div>
          <div className="bg-gray-800/50 rounded p-3 border border-gray-600">
            <div className="text-purple-300 font-medium mb-1 text-sm">Notifications</div>
            <div className="text-white text-xs">
              {settings.enableEmailNotifications || settings.enableWhatsappNotifications ? 'Active' : 'Inactive'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSettings;